// Literal port of the React helper at
// app/frontend/domains/reporting/components/dashboard/inject-sqc-widget.ts.
// Kept 1:1 so the Hotwire dashboard grid and the React dashboard place, preserve
// and gate the SQC pending-validation widget identically (CONEK-4938). The widget
// ids are inlined here (the React side imports them from widget-ids.ts) so this
// module has no dependency on the frontend TypeScript tree.

const SQC_PENDING = "SqcPending"
const KPI_HIGHLIGHTS = "KPIHighlights"

export const BREAKPOINT_KEYS = ["desktop", "mobile"]

// Grid dimensions for the SQC pending-validation widget, per breakpoint. Single
// source of truth shared by the injector (below) and the widget registry, so a
// dragged-in SQC widget and an auto-injected one are the same size.
export const SQC_WIDGET_LAYOUT = {
  desktop: { w: 6, h: 2, minW: 6, minH: 2 },
  // mobile h=4 (was 3): the stacked band would clip its fixed-height,
  // overflow:hidden cell otherwise. Kept in step with Dashboard#default_config /
  // #ise_config / #sales_manager_config (all seed mobile SqcPending at h:4);
  // preserveSeededSqcWidget re-derives the re-injected dims from this constant, so
  // a mismatch silently shrank the persisted band on every gate-off mobile save
  // (CONEK-7426 / CONEK-7615).
  mobile: { w: 2, h: 4, minW: 2, minH: 2 },
}

// Returns a copy of `config` with the SQC pending-validation widget placed
// immediately below the KPI banner in each breakpoint. Idempotent: a breakpoint
// that already has the widget is returned unchanged so the user's position is
// preserved.
export function withSqcPendingWidget(config) {
  const next = { desktop: [], mobile: [] }
  for (const breakpoint of BREAKPOINT_KEYS) {
    next[breakpoint] = injectIntoBreakpoint(
      config[breakpoint] ?? [],
      breakpoint,
    )
  }
  return next
}

function hasSqc(config) {
  return BREAKPOINT_KEYS.some((breakpoint) =>
    (config[breakpoint] ?? []).some((item) => item.i === SQC_PENDING),
  )
}

// Keeps the SQC widget in a persisted layout when its gate is off, WITHOUT
// trusting its stale coordinates. No-op when the gate is on (widget registered)
// or when the tenant never had it seeded.
export function preserveSeededSqcWidget(reported, source, registeredIds) {
  if (registeredIds.has(SQC_PENDING)) return reported
  if (!hasSqc(source)) return reported
  return withSqcPendingWidget(reported)
}

function injectIntoBreakpoint(items, breakpoint) {
  if (items.some((item) => item.i === SQC_PENDING)) {
    return [...items]
  }

  const dims = SQC_WIDGET_LAYOUT[breakpoint]
  const banner = items.find((item) => item.i === KPI_HIGHLIGHTS)
  const insertY = banner ? banner.y + banner.h : 0

  const sqcItem = {
    i: SQC_PENDING,
    x: 0,
    y: insertY,
    ...dims,
  }

  // Only push widgets down if the target band is actually occupied. When the
  // widget was seeded earlier and has since been filtered out for rendering (its
  // gate is off), its slot is still reserved — re-inserting must drop straight
  // into that gap WITHOUT shifting, otherwise every gate-off edit would shift the
  // widgets below it down again, drifting the layout unboundedly.
  const bandBottom = insertY + dims.h
  const bandOccupied = items.some(
    (item) => item.y < bandBottom && item.y + item.h > insertY,
  )
  const shifted = bandOccupied
    ? items.map((item) =>
        item.y >= insertY ? { ...item, y: item.y + dims.h } : item,
      )
    : items
  // Order by row (stable sort keeps same-row widgets in their original
  // left-to-right order).
  return [...shifted, sqcItem].sort((a, b) => a.y - b.y)
}
